/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Share2Icon } from '@radix-ui/react-icons';
import { Button } from '@/shared/ui';

interface MobileActionBarProps {
  onSchedule: () => void;
  /** Sends the listing to a client: WhatsApp, a link, an e-mail. */
  onShare: () => void;
}

/**
 * The phone's standing action bar: send the listing, and book a visit.
 *
 * These are the two things a broker does with a listing while they are not
 * at a desk — forward it to somebody, or get themselves in front of it — so
 * they sit at the bottom where the thumb is rather than inside a tab that
 * has to be found first. Booking takes the width because it is the one that
 * moves the attendance on; sending is a 48px square beside it, since its
 * icon says what it does.
 *
 * Deliberately no "Ligar": calling the owner belongs to the Propriedade
 * card, where the owner is named. A phone button here would call somebody
 * the bar never says the name of.
 *
 * It is a flex sibling of the scrolling column, not `position: fixed`. The
 * app shell pins `html` and `body` at `height:100%; overflow:hidden`, so the
 * page itself never scrolls — a fixed bar would be positioned against a
 * viewport the content does not move within, and would need the scrolling
 * column to reserve space for it by hand.
 *
 * @param props - The schedule callback and the overflow menu.
 * @returns The rendered bar.
 */
export const MobileActionBar: React.FC<MobileActionBarProps> = ({ onSchedule, onShare }) => {
  const { t } = useTranslation('features/properties');

  return (
    <div
      data-testid="property-mobile-action-bar"
      className="z-[30] flex flex-none gap-[var(--space-2)] border-t border-[var(--border-subtle)] bg-[var(--surface-primary)] px-[var(--space-4)] py-[var(--space-3)]"
    >
      <Button
        variant="outlined"
        onClick={onShare}
        aria-label={t('detailView.actions.share')}
        data-testid="property-mobile-share"
        className="h-[48px] w-[48px] flex-none p-0"
      >
        <Share2Icon width={20} height={20} />
      </Button>
      <Button
        variant="solid"
        onClick={onSchedule}
        data-testid="property-mobile-schedule"
        className="h-[48px] min-w-0 flex-1 text-[length:var(--text-body)] [font-weight:var(--font-bold)]"
      >
        {t('detailView.visits.scheduleVisit')}
      </Button>
    </div>
  );
};
